/**
 * Parsing and preview-side validation for the product import's 套裝組成 sheet.
 *
 * Kept free of React and antd so vitest can cover it: the vitest config only
 * collects src/**\/*.test.ts and runs in a `node` environment.
 *
 * The backend re-validates everything in productBundleImportValidator.ts and
 * is the authority. This mirror exists so the preview step can show errors
 * before the user commits the import — the two must stay in step, and both are
 * tested against the same rule matrix in the design doc.
 */

export interface BundleSheetRow {
  /** 1-based row number in the worksheet, for error messages */
  row: number
  parentSku: string
  componentSku: string
  quantity: number
}

export interface ResolvedSku {
  itemCode: string
  id: string
  itemName: string
  isBundle: boolean
  bundleComponentCount: number
}

export interface BundleSheetValidation {
  validBundles: Map<string, BundleSheetRow[]>
  excludedParents: Map<string, string>
  overwriteWarnings: Map<string, number>
}

const SHEET_ALIASES = ['套裝組成', '套装组成', 'bundle components', 'bundlecomponents']

const HEADER_ALIASES: Record<string, 'parentSku' | 'componentSku' | 'quantityPerSet'> = {
  套裝sku: 'parentSku',
  套装sku: 'parentSku',
  'bundle sku': 'parentSku',
  bundlesku: 'parentSku',
  parentsku: 'parentSku',
  組件sku: 'componentSku',
  组件sku: 'componentSku',
  'component sku': 'componentSku',
  componentsku: 'componentSku',
  每套數量: 'quantityPerSet',
  每套数量: 'quantityPerSet',
  'quantity per set': 'quantityPerSet',
  quantityperset: 'quantityPerSet',
  qtyperset: 'quantityPerSet',
}

const BLANK = '套裝SKU/組件SKU/每套數量不能為空'
const BAD_QUANTITY = '每套數量必須為大於 0 的整數'
const SELF_REFERENCE = '套裝SKU 不能將自己作為組件'

const norm = (value: unknown): string => String(value ?? '').trim()

const headerKey = (header: unknown) =>
  norm(header)
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // strip zero-width chars
    .toLowerCase()

export function findBundleSheetName(sheetNames: string[]): string | null {
  for (const name of sheetNames || []) {
    if (SHEET_ALIASES.includes(headerKey(name))) return name
  }
  return null
}

export function parseBundleSheetRows(grid: any[][]): BundleSheetRow[] {
  if (!Array.isArray(grid) || grid.length < 2) return []

  const headers = (grid[0] || []).map((h: any) => HEADER_ALIASES[headerKey(h)])
  const rows: BundleSheetRow[] = []

  for (let i = 1; i < grid.length; i++) {
    const values = grid[i] || []
    let parentSku = ''
    let componentSku = ''
    let rawQuantity = ''

    headers.forEach((field, column) => {
      const value = norm(values[column])
      if (field === 'parentSku') parentSku = value
      else if (field === 'componentSku') componentSku = value
      else if (field === 'quantityPerSet') rawQuantity = value
    })

    // A fully blank row is padding, not an error.
    if (!parentSku && !componentSku && !rawQuantity) continue

    rows.push({
      row: i + 1,
      parentSku,
      componentSku,
      // NaN is deliberate: validation turns it into the 每套數量 message.
      quantity: rawQuantity === '' ? NaN : Number(rawQuantity),
    })
  }

  return rows
}

export function referencedSkusOf(rows: BundleSheetRow[]): string[] {
  const skus = new Set<string>()
  for (const row of rows || []) {
    if (row.parentSku) skus.add(row.parentSku)
    if (row.componentSku) skus.add(row.componentSku)
  }
  return Array.from(skus)
}

export function validateBundleSheet({
  rows,
  incomingSkus,
  resolved,
}: {
  rows: BundleSheetRow[]
  incomingSkus: Set<string>
  resolved: ResolvedSku[]
}): BundleSheetValidation {
  const resolvedBySku = new Map<string, ResolvedSku>()
  for (const item of resolved || []) resolvedBySku.set(norm(item.itemCode), item)

  const grouped = new Map<string, BundleSheetRow[]>()
  const parentSkus = new Set<string>()

  for (const row of rows || []) {
    if (row.parentSku) parentSkus.add(row.parentSku)
    const key = row.parentSku || ''
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key)!.push(row)
  }

  const resolves = (sku: string) => incomingSkus.has(sku) || resolvedBySku.has(sku)
  const isBundleSku = (sku: string) =>
    parentSkus.has(sku) || resolvedBySku.get(sku)?.isBundle === true

  const validBundles = new Map<string, BundleSheetRow[]>()
  const excludedParents = new Map<string, string>()
  const overwriteWarnings = new Map<string, number>()

  for (const [parentSku, group] of grouped) {
    const error = firstViolation(parentSku, group, resolves, isBundleSku)
    if (error) {
      excludedParents.set(parentSku, error)
      continue
    }
    validBundles.set(parentSku, group)
    const existingCount = resolvedBySku.get(parentSku)?.bundleComponentCount ?? 0
    if (existingCount > 0) overwriteWarnings.set(parentSku, existingCount)
  }

  return { validBundles, excludedParents, overwriteWarnings }
}

/** Rules run in the design doc's numbered order so the message is the most specific one. */
function firstViolation(
  parentSku: string,
  rows: BundleSheetRow[],
  resolves: (sku: string) => boolean,
  isBundleSku: (sku: string) => boolean,
): string | null {
  if (!parentSku) return BLANK
  for (const row of rows) {
    if (!row.componentSku) return BLANK
    if (!Number.isFinite(row.quantity)) return BAD_QUANTITY
    if (!Number.isInteger(row.quantity) || row.quantity < 1) return BAD_QUANTITY
  }

  if (!resolves(parentSku)) {
    return `找不到套裝SKU「${parentSku}」，請確認已在「產品資料」工作表建立或已存在於系統`
  }

  for (const row of rows) {
    if (!resolves(row.componentSku)) {
      return `找不到組件SKU「${row.componentSku}」，請確認已在「產品資料」工作表建立或已存在於系統`
    }
  }

  for (const row of rows) {
    if (row.componentSku === parentSku) return SELF_REFERENCE
  }

  for (const row of rows) {
    if (isBundleSku(row.componentSku)) {
      return `組件SKU「${row.componentSku}」本身是套裝，暫不支援多層套裝`
    }
  }

  const seen = new Set<string>()
  for (const row of rows) {
    if (seen.has(row.componentSku)) {
      return `套裝SKU「${parentSku}」重複定義組件「${row.componentSku}」，請合併為一行`
    }
    seen.add(row.componentSku)
  }

  return null
}
